import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Pagination } from '@mui/material';

import Loader from '../../../../../../../common/elements/Loader/Loader';

import './PDFViewer.scss';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const dataUriToBlob = dataUri => {
  const [header, base64] = dataUri.split(',');
  const mimeMatch = header.match(/data:(.*);base64/);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeMatch ? mimeMatch[1] : 'application/pdf' });
};

export const PDFViewer = ({ file }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const createdUrlsRef = useRef([]);

  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
    } else if (typeof file !== 'string' || !file.startsWith('data:')) {
      setObjectUrl(file);
    } else {
      const url = URL.createObjectURL(dataUriToBlob(file));
      createdUrlsRef.current.push(url);
      setObjectUrl(url);
    }
  }, [file]);

  useEffect(
    () => () => {
      createdUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    },
    []
  );

  useEffect(() => {
    setNumPages(0);
    setIsDocumentReady(false);
  }, [objectUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setIsDocumentReady(true);
  }, []);

  const options = useMemo(
    () => ({
      cMapUrl: '/bcmaps/',
      cMapPacked: true
    }),
    []
  );

  return (
    <>
      <Pagination
        showFirstButton
        showLastButton
        size="small"
        sx={{
          margin: '17px 0 15px 0',
          '.MuiButtonBase-root': {
            outline: 'none'
          }
        }}
        count={numPages}
        page={pageNumber}
        onChange={(event, num) => setPageNumber(num)}
      />
      <div className="pdv-viewer">
        {objectUrl && (
          <Document
            key={objectUrl}
            options={options}
            file={objectUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<Loader dark={true} />}
            noData={<Loader dark={true} />}
          >
            {isDocumentReady && (
              <Page
                key={`page_${pageNumber}`}
                pageNumber={pageNumber}
                height={766}
                loading={<Loader dark={true} />}
                error={<Loader dark={true} />}
              />
            )}
          </Document>
        )}
      </div>
    </>
  );
};
